import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useSettings } from './useSettings';
import { useAuth } from '../context/AuthContext';

/**
 * Hook que sincroniza el idioma de i18next con los ajustes del usuario
 * Esto permite que el idioma seleccionado se guarde en la base de datos
 * y se mantenga entre sesiones
 */
export const useLanguageSync = () => {
  const { i18n } = useTranslation();
  const { isAuthenticated } = useAuth();
  const { settings, updateSetting, loading } = useSettings();

  // Sincronizar idioma de ajustes con i18next cuando cargan los ajustes
  useEffect(() => {
    if (isAuthenticated && !loading && settings.language && i18n.language !== settings.language) {
      i18n.changeLanguage(settings.language);
    }
  }, [isAuthenticated, settings.language, i18n, loading]);

  // Guardar cambios de idioma en los ajustes
  useEffect(() => {
    if (!isAuthenticated) {
      return;
    }

    const handleLanguageChange = async (lng: string) => {
      if (!loading && lng !== settings.language) {
        try {
          await updateSetting('language', lng);
        } catch (error) {
          console.error('LanguageSync: Error saving language setting:', error);
        }
      }
    };

    // Escuchar cambios de idioma
    i18n.on('languageChanged', handleLanguageChange);

    // Cleanup
    return () => {
      i18n.off('languageChanged', handleLanguageChange);
    };
  }, [isAuthenticated, settings.language, updateSetting, loading, i18n]);

  return {
    currentLanguage: i18n.language,
    isLoading: loading
  };
};